import { useState } from 'react'

import { Form } from '@tribeplatform/react-sdk/components'
import {
  useAuthMember,
  useResendVerificationCode,
  useVerifyMember,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useRefreshToken } from '../../hooks/useRefreshToken'
import { onAuthenticate } from './utils/onAuthenticate'

export const VerifyForm = ({ memberId }: { memberId?: string }) => {
  const { update: updateRefreshToken } = useRefreshToken()

  const { data: member, isGuest } = useAuthMember()

  const { mutateAsync: resend, isLoading: resendIsLoading } =
    useResendVerificationCode()

  const { mutateAsync: verify, isLoading } = useVerifyMember()

  const [showResend, setShowResend] = useState(true)

  if (!memberId) memberId = member?.id

  const onResend = async () => {
    const result = await resend()
    if (result?.status === 'succeeded') {
      setShowResend(false)
      toast({
        title: 'Verification email sent',
        description:
          "Please check your spam if you couldn't find the email in your inbox.",
        status: 'neutral',
      })
    } else {
      toast({
        title: 'Could not send verification email',
        description: 'Something went wrong. Please try again later.',
        status: 'error',
      })
    }
  }

  const onSubmit = async data => {
    return verify(
      {
        input: {
          memberId,
          verificationCode: data?.verificationCode,
        },
      },
      {
        onSuccess: async authToken => {
          const newAuthToken = await updateRefreshToken(authToken)
          const { refreshToken, accessToken } = newAuthToken || {}
          if (accessToken || refreshToken) {
            onAuthenticate()
            window.location.href = '/'
          }
        },
      },
    )
  }

  return (
    <Form onSubmit={onSubmit}>
      {methods => (
        <>
          <p className="mb-5">
            {isGuest ? (
              <>
                We&apos;ve sent you a verification email. Please enter the code
                here.
              </>
            ) : (
              <>
                We&apos;ve sent a verification email to{' '}
                <strong>{member?.email}</strong>. Please enter the code here.
              </>
            )}
          </p>
          <Form.Input
            name="verificationCode"
            label="Verification code"
            placeholder="5-digit alpha-numeric code"
            autoComplete="off"
            onChange={e => {
              if (e.target.value.length === 5) {
                methods.submitForm({
                  verificationCode: e.target.value,
                })
              }
            }}
            validation={{
              required: 'Verification code is required.',
            }}
          />

          <Form.Actions className="mt-5">
            <Button type="submit" size="lg" loading={isLoading}>
              Verify
            </Button>
            {showResend ? (
              <Button
                size="lg"
                disabled={isLoading || resendIsLoading}
                variant="outline"
                onClick={onResend}
              >
                Resend
              </Button>
            ) : null}
          </Form.Actions>
        </>
      )}
    </Form>
  )
}
